import { Input, StarsButton, Translation } from '@ps/web-shared-components';
import { StarsLink } from '@ps/web-shared-ui';
import { allowedLength, numbersOnly, required } from '@ps/web-shared-validators';
import classNames from 'clsx';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-final-form';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import ButtonGroup from '@swf/components/ButtonGroup/ButtonGroup';
import BackButton from '@swf/components/StarsModal/components/BackButton';
import useWizardJump from '@swf/core/hooks/useWizardJump';
import type { Dispatch } from '@swf/core/store';

import {
  TXTCLI_Enter_N0_digit_number,
  TXTCLI_Get_a_new_code_in_N0,
  TXTCLI_Submit,
  TXTWCL_A_verification_code_has_been_s_ELL,
  TXTWCL_DidnXt_receive_the_codeX,
  TXTWCL_Re_send_code,
  TXTWCL_The_validation_code_you_have_e_ELL,
  TXTWCL_Verification_code,
  TXTWCL_Verify_later
} from './ValidateCode.messages.json';
import styles from './ValidateCode.scss';

interface Props {
  first?: boolean;
  goBack?(): void;
  showVerifyLater?: boolean;
  isSiteSpecific?: boolean;
}

const INITIAL_SECONDS = 60;
const MAX_LENGTH = 6;

const ValidateCode: React.FC<Props> = ({
  first,
  goBack,
  showVerifyLater = true,
  isSiteSpecific
}) => {
  const { jumpForward } = useWizardJump();
  const { formatMessage } = useIntl();
  const {
    account: { resendVerificationCode }
  } = useDispatch<Dispatch>();
  const form = useForm();

  const [secondsToResend, setSecondsToResend] = useState<number>(INITIAL_SECONDS);

  useEffect(() => {
    form.change('validationCode', '');
  }, [form]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    if (secondsToResend > 0) {
      timeout = setTimeout(() => {
        setSecondsToResend((seconds) => seconds - 1);
      }, 1000);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [secondsToResend]);

  const { modal } = useDispatch<Dispatch>();

  const handleVerifyLater = () =>
    first ? jumpForward('#signup.signup-legal') : modal.close('/notifications/verify-code');

  const onResendCode = async () => {
    await resendVerificationCode();
    setSecondsToResend(INITIAL_SECONDS);
  };

  return (
    <>
      {!first && <BackButton onClick={goBack} />}
      <p className={styles.paragraph}>
        <Translation {...TXTWCL_A_verification_code_has_been_s_ELL} />
      </p>
      <Input
        type="tel"
        maxLength={6}
        id="validationCode"
        label={TXTWCL_Verification_code}
        placeholder={
          isSiteSpecific ? '' : formatMessage(TXTCLI_Enter_N0_digit_number, { 0: MAX_LENGTH })
        }
        validators={[
          required(),
          isSiteSpecific
            ? numbersOnly(TXTWCL_The_validation_code_you_have_e_ELL)
            : allowedLength([6], TXTWCL_The_validation_code_you_have_e_ELL)
        ]}
      />
      <ButtonGroup className={classNames({ [styles.firstGroup]: first })}>
        <StarsButton
          className={classNames({ [styles.submit]: first })}
          type={first ? 'submit' : 'button'}
          translation={TXTCLI_Submit}
        />
        {isSiteSpecific ? (
          <StarsButton
            disabled={secondsToResend > 0}
            variant="secondary"
            onClick={onResendCode}
            translation={secondsToResend > 0 ? TXTCLI_Get_a_new_code_in_N0 : TXTWCL_Re_send_code}
            values={{ 0: secondsToResend }}
          />
        ) : (
          <StarsButton variant="secondary" translation={TXTWCL_Re_send_code} />
        )}
      </ButtonGroup>
      {showVerifyLater && (
        <div className={styles.bottomText}>
          <Translation {...TXTWCL_DidnXt_receive_the_codeX} />{' '}
          <StarsLink
            className={classNames({ [styles.verifyLater]: !first })}
            onClick={handleVerifyLater}
          >
            <Translation {...TXTWCL_Verify_later} />
          </StarsLink>
        </div>
      )}
    </>
  );
};

export default ValidateCode;
